import { getSuperAdminToken } from "../context/superadmin/superadmin-storage";
import {
  BadRequestError,
  NetworkError,
  NotAuthorizedError,
} from "./network-error";
import { baseURL } from "./api";

export const createSuperAdminHeaders = ({ auth = false, json = true } = {}) => {
  const headers = {
    "Cache-Control": "no-cache",
  };

  if (json) {
    headers["Content-Type"] = "application/json";
  }

  if (auth) {
    const token = getSuperAdminToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

const buildUrl = (path) => `${baseURL}/${path}`;

const parseResponseBody = async (response) => {
  const text = await response.text();
  if (text === "") {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    throw new NetworkError("Unable to parse server response", response.status);
  }
};

const parseError = (response, statusCode) => {
  const message = response?.message;
  if (statusCode === 401 || statusCode === 403) {
    throw new NotAuthorizedError(message ?? "Not authorized", statusCode);
  }
  if (statusCode === 400) {
    throw new BadRequestError(message ?? response?.errors ?? "Bad request");
  }
  if (statusCode === 404) {
    throw new NetworkError(message ?? "Resource not found", statusCode);
  }
  throw new NetworkError(
    message ?? "Unable to process your request",
    statusCode
  );
};

export const superAdminRequest = async ({
  path,
  method,
  body,
  auth = true,
  json = true,
}) => {
  let response;
  try {
    response = await fetch(buildUrl(path), {
      method,
      body,
      headers: createSuperAdminHeaders({ auth, json }),
    });
  } catch (e) {
    throw new NetworkError("Unable to connect to the server");
  }

  const result = await parseResponseBody(response);

  if (response.status >= 200 && response.status < 300) {
    return result;
  }

  parseError(result, response.status);
};

export const superAdminGet = (path) =>
  superAdminRequest({ path, method: "GET" });

export const superAdminPost = (path, data, auth = true) =>
  superAdminRequest({
    path,
    method: "POST",
    body: JSON.stringify(data),
    auth,
  });

export const superAdminPatch = (path, data) =>
  superAdminRequest({
    path,
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const superAdminPut = (path, data) =>
  superAdminRequest({
    path,
    method: "PUT",
    body: JSON.stringify(data),
  });

export const superAdminDelete = (path) =>
  superAdminRequest({ path, method: "DELETE" });

export const superAdminUpload = (path, data, method = "PUT") =>
  superAdminRequest({
    path,
    method,
    body: data,
    json: false,
  });
